import { useCallback, useMemo, useRef } from "react"

import usePageConfig from "Pods/Shared/Hooks/usePageConfig"
import { DEFAULT_SURROUNDING_ID } from "Pods/ThreeDStudio/constants"
import useRequireLogin from "Pods/ThreeDStudio/Hooks/useRequireLogin"
import useUpdateSessionSettings from "Pods/ThreeDStudio/Hooks/useUpdateSessionSettings"
import { useViewerStore } from "Pods/ThreeDStudio/Hooks/useViewerStore"
import { useViewerContext } from "Pods/ThreeDStudio/Shared/ViewerContext/ViewerContext"
import { useThreeDStudioStore } from "Pods/ThreeDStudio/Store"

import useBackgroundImages from "./useBackgroundImages"
import useUploadBackgroundImage from "./useUploadBackgroundImage"

function useEnvironmentSettings() {
  const fileRef = useRef(null)
  const viewer = useViewerContext()
  const environment = useViewerStore(viewer, "environment")
  const { images, isLoading: isOptionsLoading } = useBackgroundImages()
  const { uploadBackgroundImage, isUploading } = useUploadBackgroundImage()
  const updateSessionSettings = useUpdateSessionSettings()
  const requireLogin = useRequireLogin()
  const { isCurrentUser: isLoggedIn } = usePageConfig() || {}
  const persistedSurroundingId = useThreeDStudioStore(
    state => state.ui?.openedModel?.settings?.surroundingId ?? null,
  )
  // Default to Studio Soft Light only if the API response confirms it exists.
  const surroundingId = useMemo(() => {
    if (persistedSurroundingId != null) {
      return persistedSurroundingId
    }

    const defaultExists = images.some(image => String(image.id) === String(DEFAULT_SURROUNDING_ID))

    return defaultExists ? DEFAULT_SURROUNDING_ID : null
  }, [persistedSurroundingId, images])

  const update = useCallback(
    value => viewer?.setEnvironmentSettings(value),
    [viewer],
  )

  // Uploading an HDRI requires an account; prompt a logged-out user to sign up
  // instead of opening the file picker.
  const handleUploadClick = useCallback(() => {
    if (requireLogin()) {
      fileRef.current?.click()
    }
  }, [requireLogin])

  const handleFile = useCallback(
    event => {
      const { target } = event
      const file = target.files?.[0]
      target.value = ""
      if (!file || !viewer) {
        return
      }

      viewer.loadEnvironment(file).then(() => {
        if (!environment?.showBackground) {
          update({ showBackground: true })
        }
      }).catch(() => {})

      uploadBackgroundImage(file)
    },
    [viewer, environment?.showBackground, update, uploadBackgroundImage],
  )

  const handlePresetChange = useCallback(
    url => {
      viewer?.loadEnvironment(url).catch(() => {})
      const id = images.find(image => image.background_url === url)?.id
      if (id != null) {
        updateSessionSettings({ surroundingId: id })
      }
    },
    [viewer, environment?.showBackground, update, images, updateSessionSettings],
  )

  const handleVisibilityChange = useCallback(
    checked => {
      update({ showBackground: checked })
      updateSessionSettings({ showHdri: checked })
    },
    [update, updateSessionSettings],
  )

  const handleIntensityChange = useCallback(
    value => {
      const clamped = Math.min(value, 5)
      update({ intensity: clamped })
      updateSessionSettings({ strength: clamped }, { debounceMs: 300 })
    },
    [update, updateSessionSettings],
  )

  const handleRotationChange = useCallback(
    value => {
      const clamped = Math.min(value, 360)
      update({ rotation: clamped })
      updateSessionSettings({ rotation: clamped }, { debounceMs: 300 })
    },
    [update, updateSessionSettings],
  )

  const options = useMemo(
    () => images.map(image => ({
      value: image.background_url,
      label: image.label,
    })),
    [images],
  )

  const selectedSurroundingUrl = useMemo(
    () => images.find(image => String(image.id) === String(surroundingId))?.background_url ?? null,
    [images, surroundingId],
  )

  return {
    fileRef,
    environment,
    hasViewerEnvironment: Boolean(viewer && environment),
    isLoggedIn: Boolean(isLoggedIn),
    options,
    selectedSurroundingUrl,
    isOptionsLoading,
    isUploading,
    handleFile,
    handleUploadClick,
    handlePresetChange,
    handleVisibilityChange,
    handleIntensityChange,
    handleRotationChange,
  }
}

export default useEnvironmentSettings
